import React, { useMemo } from "react";

import Label from "@busbud/design-system-components/dist/Label";
import Seat from "@busbud/design-system-components/dist/Seat";
import type { SeatBaseState } from "@busbud/design-system-components/dist/SeatBase";
import Stack from "@busbud/design-system-components/dist/Stack";
import type { Choices } from "@busbud/int-schemas/lib";
import type * as UDM from "@busbud/int-schemas/udm";

import { ANY, UDMGender } from "@app/constants/passengers";
import { useAppContext } from "@app/helpers/hooks";

import { useStyles } from "./seat-gender-selection.styles";

interface Props {
  className?: string;
  horizontal?: boolean;
  allowed_gender?: UDM.SeatCell["gender"];
  onGenderSelect: (gender: Choices.Gender) => void;
  first_column?: boolean;
  last_row?: boolean;
}

type SelectionButton = {
  callback: () => void;
  state: SeatBaseState;
  test_id: string;
  text: string;
};

const isDisabled = (
  state: SeatBaseState,
  allowed_gender?: UDM.SeatCell["gender"]
) =>
  (state === "unavailableWoman" && allowed_gender === UDMGender.Male) ||
  (state === "unavailableMan" && allowed_gender === UDMGender.Female);

export const SeatGenderSelection: React.FC<Props> = props => {
  const {
    allowed_gender = ANY,
    onGenderSelect,
    horizontal = false,
    first_column = false,
    last_row = false
  } = props;

  const { translator } = useAppContext();
  const { cx, classes } = useStyles();
  const data = useMemo<SelectionButton[]>(
    () => [
      {
        text: translator.t("!seat-legend.woman"),
        state: "unavailableWoman",
        callback: () =>
          allowed_gender !== UDMGender.Male && onGenderSelect(UDMGender.Female),
        test_id: "female"
      },
      {
        text: translator.t("!seat-legend.man"),
        state: "unavailableMan",
        callback: () =>
          allowed_gender !== UDMGender.Female && onGenderSelect(UDMGender.Male),
        test_id: "male"
      }
    ],
    [allowed_gender, onGenderSelect, translator]
  );

  return (
    <Stack
      alignSelf="center"
      className={cx(classes.root, props.className, {
        [classes.vertical]: !horizontal,
        [classes.horizontal]: horizontal,
        [classes.firstRow]: first_column,
        [classes.lastColumn]: last_row
      })}
      spacing={0.25}
    >
      {data.map(({ state, text, callback, test_id }) => (
        <Stack
          key={state}
          role="button"
          onClick={callback}
          data-cy={`select-gender-${test_id}`}
          alignItems="center"
          direction="column"
          spacing={0.5}
          className={cx(classes.item, {
            [classes.disabled]: isDisabled(state, allowed_gender),
            [classes.left]: state === "unavailableWoman",
            [classes.right]: state === "unavailableMan"
          })}
        >
          <Seat
            state={state}
            size="sm"
            className={classes.seat}
            classes={
              !isDisabled(state, allowed_gender)
                ? { unavailable: classes.available }
                : undefined
            }
          />
          <Label
            size="sm"
            fontWeight="bold"
            color="text.secondary.opaque"
            data-testid={`${test_id}-option`}
          >
            {text}
          </Label>
        </Stack>
      ))}
    </Stack>
  );
};
